import { useCallback, useEffect, useRef, useState } from "react";
import { useMediaQuery, useTheme } from "@mui/material";
import useEmblaCarousel from "embla-carousel-react";
import { cardsData } from "./NextStepsSection.config";

export interface UseNextStepCarouselReturn {
  // Embla
  emblaRef: ReturnType<typeof useEmblaCarousel>[0];
  emblaApi: ReturnType<typeof useEmblaCarousel>[1];
  // State
  selectedIndex: number;
  activeCardIndex: number; // Maps scroll progress to actual card indices
  snapCount: number;
  isThumbDragging: boolean;
  // Refs
  trackRef: React.RefObject<HTMLDivElement>;
  // Handlers
  handleThumbPointerDown: (event: React.PointerEvent<HTMLDivElement>) => void;
  handleThumbPointerMove: (event: React.PointerEvent<HTMLDivElement>) => void;
  handleThumbPointerUp: (event: React.PointerEvent<HTMLDivElement>) => void;
  handleTrackClick: (event: React.PointerEvent<HTMLDivElement>) => void;
  handleIndicatorClick: (index: number) => void;
}

export const useNextStepCarousel = (): UseNextStepCarouselReturn => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"), {
    defaultMatches: true,
  });

  // State
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [activeCardIndex, setActiveCardIndex] = useState(0);
  const [isThumbDragging, setIsThumbDragging] = useState(false);
  const [snapCount, setSnapCount] = useState(cardsData.length);

  // Refs
  const trackRef = useRef<HTMLDivElement>(null);
  const dragStartXRef = useRef<number>(0);
  const dragStartIndexRef = useRef<number>(0);

  // Embla instance
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: isMobile ? "center" : "start",
    containScroll: "trimSnaps",
    loop: false,
    dragFree: !isMobile,
  });

  // Sync selected index with Embla (fires when scroll settles)
  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());

    // Also update active card index based on current scroll progress
    const progress = emblaApi.scrollProgress();
    const totalCards = cardsData.length;
    const rawCardIndex = progress * (totalCards - 1);
    const cardIndex = Math.round(rawCardIndex);
    setActiveCardIndex(Math.max(0, Math.min(totalCards - 1, cardIndex)));
  }, [emblaApi]);

  // Continuous sync during carousel drag
  const onScroll = useCallback(() => {
    if (!emblaApi || isThumbDragging) return;

    const progress = emblaApi.scrollProgress();
    const scrollSnapList = emblaApi.scrollSnapList();

    // Find closest snap index for progress bar
    let closestIndex = 0;
    let minDistance = Math.abs(progress - scrollSnapList[0]);

    for (let i = 1; i < scrollSnapList.length; i++) {
      const distance = Math.abs(progress - scrollSnapList[i]);
      if (distance < minDistance) {
        minDistance = distance;
        closestIndex = i;
      }
    }

    setSelectedIndex(closestIndex);

    // Calculate active card index based on scroll progress
    // This maps the 0-1 progress to the full range of card indices
    const totalCards = cardsData.length;
    const rawCardIndex = progress * (totalCards - 1);
    const cardIndex = Math.round(rawCardIndex);
    setActiveCardIndex(Math.max(0, Math.min(totalCards - 1, cardIndex)));
  }, [emblaApi, isThumbDragging]);

  // Update snap count when Embla reinitializes
  const onReInit = useCallback(() => {
    if (!emblaApi) return;
    const snaps = emblaApi.scrollSnapList().length;
    setSnapCount(snaps);
    setSelectedIndex(emblaApi.selectedScrollSnap());

    // Also update active card index
    const progress = emblaApi.scrollProgress();
    const totalCards = cardsData.length;
    const rawCardIndex = progress * (totalCards - 1);
    const cardIndex = Math.round(rawCardIndex);
    setActiveCardIndex(Math.max(0, Math.min(totalCards - 1, cardIndex)));
  }, [emblaApi]);

  // Setup Embla event listeners
  useEffect(() => {
    if (!emblaApi) return;

    emblaApi.on("select", onSelect);
    emblaApi.on("scroll", onScroll);
    emblaApi.on("reInit", onReInit);

    const snaps = emblaApi.scrollSnapList().length;
    setSnapCount(snaps);
    onSelect();

    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("scroll", onScroll);
      emblaApi.off("reInit", onReInit);
    };
  }, [emblaApi, onSelect, onScroll, onReInit]);

  // Calculate slot from position
  const calculateSlotFromPosition = useCallback(
    (clientX: number): number => {
      if (!trackRef.current) return selectedIndex;

      const trackRect = trackRef.current.getBoundingClientRect();
      const relativeX = clientX - trackRect.left;
      const slotWidth = trackRect.width / snapCount;
      const slotIndex = Math.floor(relativeX / slotWidth);

      return Math.max(0, Math.min(snapCount - 1, slotIndex));
    },
    [selectedIndex, snapCount]
  );

  // Thumb pointer handlers
  const handleThumbPointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (event.pointerType === "mouse" && event.button !== 0) return;

      event.preventDefault();
      event.stopPropagation();
      event.currentTarget.setPointerCapture(event.pointerId);

      dragStartXRef.current = event.clientX;
      dragStartIndexRef.current = selectedIndex;
      setIsThumbDragging(true);
    },
    [selectedIndex]
  );

  const handleThumbPointerMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!event.currentTarget.hasPointerCapture(event.pointerId)) return;
      if (!trackRef.current || !emblaApi) return;

      const trackRect = trackRef.current.getBoundingClientRect();
      const slotWidth = trackRect.width / snapCount;
      const dragDelta = event.clientX - dragStartXRef.current;
      const slotDelta = Math.round(dragDelta / slotWidth);

      const targetIndex = Math.max(
        0,
        Math.min(snapCount - 1, dragStartIndexRef.current + slotDelta)
      );

      if (targetIndex !== emblaApi.selectedScrollSnap()) {
        emblaApi.scrollTo(targetIndex);
      }
    },
    [emblaApi, snapCount]
  );

  const handleThumbPointerUp = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
      setIsThumbDragging(false);
    },
    []
  );

  // Track click handler
  const handleTrackClick = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (event.target !== event.currentTarget) return;

      event.preventDefault();
      event.stopPropagation();

      if (!emblaApi) return;

      const targetIndex = calculateSlotFromPosition(event.clientX);
      emblaApi.scrollTo(targetIndex);
    },
    [emblaApi, calculateSlotFromPosition]
  );

  // Indicator click handler
  const handleIndicatorClick = useCallback(
    (index: number) => {
      if (!emblaApi) return;
      emblaApi.scrollTo(index);
    },
    [emblaApi]
  );

  return {
    emblaRef,
    emblaApi,
    selectedIndex,
    activeCardIndex,
    snapCount,
    isThumbDragging,
    trackRef,
    handleThumbPointerDown,
    handleThumbPointerMove,
    handleThumbPointerUp,
    handleTrackClick,
    handleIndicatorClick,
  };
};

